import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { db, auth } from './firebase';
import { doc, getDoc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import UnifiedDiscoveryFeed from './components/UnifiedDiscoveryFeed';
import PostLink from './components/PostLink';
import CampaignFeedback from './components/CampaignFeedback';
import BuyTokens from './components/BuyTokens';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancel from './components/PaymentCancel';
import Login from './components/Login';
import Signup from './components/Signup';
import InterestSettings from './components/InterestSettings';
import Stage13Control from './components/Stage13Control';
import { normalizeInterestIds } from './services/discoveryConfig';
import { ensureSignupGrant } from './services/signupGrantClient';

function AppContent() {
  const { user, loading } = useAuth();

  const getInitialView = () => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (
      process.env.NODE_ENV !== 'production' &&
      (path.includes('stage13-control') || params.get('stage13-control') === 'true')
    ) return 'stage13Control';
    if (path.includes('payment-success')) return 'paymentSuccess';
    if (path.includes('payment-cancel')) return 'paymentCancel';
    return 'discoveryFeed';
  };

  const [currentView, setCurrentView] = useState(getInitialView());
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [userTokens, setUserTokens] = useState(0);
  const [userProfile, setUserProfile] = useState(null);
  const [showSignup, setShowSignup] = useState(false);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            await ensureSignupGrant().catch((grantError) => {
              console.error('Error ensuring signup grant:', grantError);
            });

            const refreshedUserDoc = await getDoc(userDocRef);
            const userData = refreshedUserDoc.exists() ? refreshedUserDoc.data() : userDoc.data();
            setUserProfile(userData);
            setUserTokens(userData.tokens || 0);
          } else {
            const now = new Date().toISOString();
            const newUserProfile = {
              uid: user.uid,
              email: user.email,
              interestIds: [],
              interestVersion: 1,
              onboardingCompletedAt: null,
              completedLinks: [],
              joinDate: now,
              lastActive: now
            };

            await setDoc(userDocRef, newUserProfile);
            await ensureSignupGrant().catch((grantError) => {
              console.error('Error ensuring signup grant:', grantError);
            });

            const refreshedUserDoc = await getDoc(userDocRef);
            if (refreshedUserDoc.exists()) {
              const userData = refreshedUserDoc.data();
              setUserProfile(userData);
              setUserTokens(userData.tokens || 0);
            } else {
              setUserProfile(newUserProfile);
              setUserTokens(0);
            }
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
          setUserTokens(0);
        }
      } else {
        setUserProfile(null);
        setUserTokens(0);
        setCurrentView('discoveryFeed');
      }
    };

    if (!loading) loadUserProfile();
  }, [user, loading]);

  useEffect(() => {
    if (!user) return undefined;

    const userDocRef = doc(db, 'users', user.uid);
    return onSnapshot(userDocRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const userData = snapshot.data();
      setUserProfile(userData);
      setUserTokens(userData.tokens || 0);
    }, (error) => {
      console.error('Error listening for user profile updates:', error);
    });
  }, [user]);

  const handleNavigateToPostLink = () => {
    setEditingCampaign(null);
    setCurrentView('postLink');
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCurrentView('postLink');
  };

  const handleNavigateToBuyTokens = () => {
    setCurrentView('buyTokens');
  };

  const handleNavigateToCampaignFeedback = () => {
    setEditingCampaign(null);
    setCurrentView('campaignFeedback');
  };

  const handleNavigateToProfile = () => {
    setEditingCampaign(null);
    setCurrentView('profileSettings');
  };

  const handleNavigateToHome = () => {
    setEditingCampaign(null);
    setCurrentView('discoveryFeed');
  };

  const handleUserProfileRefresh = (profile) => {
    if (!profile) return;
    setUserProfile(profile);
    setUserTokens(profile.tokens || 0);
  };

  const handleSaveInterests = async (interestIds) => {
    if (!user || !userProfile) return;

    const normalizedIds = normalizeInterestIds(interestIds);
    const now = new Date().toISOString();
    const userDocRef = doc(db, 'users', user.uid);

    await updateDoc(userDocRef, {
      interestIds: normalizedIds,
      interestVersion: 1,
      onboardingCompletedAt: userProfile.onboardingCompletedAt || now,
      lastActive: now
    });

    setUserProfile((profile) => ({
      ...profile,
      interestIds: normalizedIds,
      interestVersion: 1,
      onboardingCompletedAt: profile.onboardingCompletedAt || now,
      lastActive: now
    }));
    setCurrentView('discoveryFeed');
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={{ textAlign: 'center', fontSize: 18, color: '#666' }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>Loading</div>
          Loading PromoGrind...
        </div>
      </div>
    );
  }

  if (!user) {
    return showSignup ? (
      <Signup onSwitchToLogin={() => setShowSignup(false)} />
    ) : (
      <Login onSwitchToSignup={() => setShowSignup(true)} />
    );
  }

  const needsDiscoveryInterestOnboarding =
    currentView === 'discoveryFeed' &&
    userProfile &&
    normalizeInterestIds(userProfile.interestIds).length === 0;

  return (
    <div style={appStyle}>
      <nav style={navStyle}>
        <div style={navLeftStyle}>
          <h1 onClick={handleNavigateToHome} style={brandStyle}>PromoGrind</h1>

          <div style={tabRowStyle}>
            <NavButton active={currentView === 'discoveryFeed'} color="#007bff" onClick={handleNavigateToHome}>
              Discovery
            </NavButton>
            <NavButton active={currentView === 'postLink'} color="#28a745" onClick={handleNavigateToPostLink}>
              Create Campaign
            </NavButton>
            <NavButton active={currentView === 'profileSettings'} color="#0f766e" onClick={handleNavigateToProfile}>
              Profile
            </NavButton>
            <NavButton active={currentView === 'buyTokens'} color="#ffc107" activeTextColor="#111" onClick={handleNavigateToBuyTokens}>
              Buy Tokens
            </NavButton>
            <NavButton active={currentView === 'campaignFeedback'} color="#17a2b8" onClick={handleNavigateToCampaignFeedback}>
              My Campaigns
            </NavButton>
          </div>
        </div>

        <div style={userControlsStyle}>
          <div style={tokenBadgeStyle}>{userTokens} Tokens</div>
          <div>
            <strong style={{ color: '#333', fontSize: 14 }}>{user.email?.split('@')[0]}</strong>
            <button type="button" onClick={handleSignOut} style={signOutStyle}>Sign Out</button>
          </div>
        </div>
      </nav>

      <main>
        {currentView === 'discoveryFeed' && needsDiscoveryInterestOnboarding && (
          <InterestSettings
            initialInterestIds={userProfile?.interestIds || []}
            mode="onboarding"
            onSave={handleSaveInterests}
          />
        )}

        {currentView === 'discoveryFeed' && !needsDiscoveryInterestOnboarding && (
          <UnifiedDiscoveryFeed
            userProfile={userProfile}
            onNavigateToProfile={handleNavigateToProfile}
            onUserProfileRefresh={handleUserProfileRefresh}
          />
        )}

        {currentView === 'postLink' && (
          <PostLink
            key={editingCampaign?.id || 'new-campaign'}
            userTokens={userTokens}
            userProfile={userProfile}
            onNavigateToHome={handleNavigateToHome}
            onNavigateToBuyTokens={handleNavigateToBuyTokens}
            editingCampaign={editingCampaign}
          />
        )}

        {currentView === 'campaignFeedback' && (
          <CampaignFeedback
            userProfile={userProfile}
            onNavigateToPostLink={handleNavigateToPostLink}
            onEditCampaign={handleEditCampaign}
          />
        )}

        {currentView === 'profileSettings' && (
          <InterestSettings
            initialInterestIds={userProfile?.interestIds || []}
            mode="settings"
            onSave={handleSaveInterests}
            onCancel={handleNavigateToHome}
          />
        )}

        {currentView === 'buyTokens' && (
          <BuyTokens
            userTokens={userTokens}
            userProfile={userProfile}
            onNavigateToHome={handleNavigateToHome}
            onNavigateToPostLink={handleNavigateToPostLink}
          />
        )}

        {currentView === 'paymentSuccess' && (
          <PaymentSuccess
            userProfile={userProfile}
            onNavigateToHome={handleNavigateToHome}
          />
        )}

        {currentView === 'paymentCancel' && (
          <PaymentCancel
            onNavigateToBuyTokens={handleNavigateToBuyTokens}
            onNavigateToHome={handleNavigateToHome}
          />
        )}

        {currentView === 'stage13Control' && process.env.NODE_ENV !== 'production' && (
          <Stage13Control
            user={user}
            userProfile={userProfile}
            onSignOut={handleSignOut}
          />
        )}
      </main>
    </div>
  );
}

function NavButton({ active, color, activeTextColor = '#fff', onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        backgroundColor: active ? color : 'transparent',
        border: `2px solid ${color}`,
        borderRadius: 25,
        color: active ? activeTextColor : color,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 'bold',
        padding: '8px 16px'
      }}
    >
      {children}
    </button>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

const loadingStyle = {
  alignItems: 'center',
  backgroundColor: '#f5f5f5',
  display: 'flex',
  height: '100vh',
  justifyContent: 'center'
};

const appStyle = {
  backgroundColor: '#f5f5f5',
  fontFamily: 'Arial, sans-serif',
  minHeight: '100vh'
};

const navStyle = {
  alignItems: 'center',
  backgroundColor: '#ffffff',
  borderBottom: '2px solid #007bff',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  display: 'flex',
  gap: 16,
  justifyContent: 'space-between',
  padding: '15px 30px'
};

const navLeftStyle = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 15
};

const brandStyle = {
  color: '#007bff',
  cursor: 'pointer',
  fontSize: 24,
  fontWeight: 'bold',
  margin: 0
};

const tabRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 5
};

const userControlsStyle = {
  alignItems: 'center',
  display: 'flex',
  gap: 15
};

const tokenBadgeStyle = {
  backgroundColor: '#f8f9fa',
  border: '2px solid #007bff',
  borderRadius: 20,
  color: '#007bff',
  fontSize: 16,
  fontWeight: 'bold',
  padding: '8px 16px'
};

const signOutStyle = {
  backgroundColor: 'transparent',
  border: 'none',
  color: '#666',
  cursor: 'pointer',
  display: 'block',
  fontSize: 12,
  padding: 0,
  textDecoration: 'underline'
};

export default App;
